import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';
import { useLanguage } from '../i18n';

const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langAnchorEl, setLangAnchorEl] = useState<null | HTMLElement>(null);
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: t.navbar.features, href: '#features' },
    { label: t.navbar.howItWorks, href: '#how-it-works' },
  ];

  const handleNavClick = (href: string) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box
      component="nav"
      id="landing-navbar"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1100,
        px: { xs: 2, md: 4, lg: 6 },
        py: 1.5,
        transition: 'all 0.3s ease',
        background: scrolled
          ? 'rgba(10, 14, 39, 0.92)'
          : 'transparent',
        borderBottom: scrolled ? '1px solid rgba(139, 92, 246, 0.15)' : 'none',
      }}
    >
      <Box
        sx={{
          maxWidth: 1280,
          mx: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {/* Logo */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            cursor: 'pointer',
          }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          <Box
            component="img"
            src="/image.png"
            alt="KrElixir Technology"
            sx={{
              height: 40,
              width: 'auto',
              filter: 'brightness(0) invert(1)',
            }}
          />
        </Box>

        {/* Desktop Nav */}
        {!isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {navLinks.map((link) => (
              <Button
                key={link.href}
                onClick={() => handleNavClick(link.href)}
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  px: 2,
                  '&:hover': {
                    color: '#fff',
                    background: 'rgba(255,255,255,0.05)',
                  },
                }}
              >
                {link.label}
              </Button>
            ))}

            {/* Language Toggle */}
            <IconButton
              onClick={(e) => setLangAnchorEl(e.currentTarget)}
              sx={{
                color: 'rgba(255,255,255,0.7)',
                '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.05)' },
              }}
            >
              <LanguageIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={langAnchorEl}
              open={Boolean(langAnchorEl)}
              onClose={() => setLangAnchorEl(null)}
              PaperProps={{
                sx: {
                  mt: 1,
                  background: 'rgba(26, 31, 58, 0.95)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(139, 92, 246, 0.2)',
                  borderRadius: 2,
                  minWidth: 150,
                },
              }}
            >
              <MenuItem
                onClick={() => { setLanguage('en'); setLangAnchorEl(null); }}
                selected={language === 'en'}
                sx={{ gap: 1, color: '#fff', '&.Mui-selected': { background: alpha('#8B5CF6', 0.2) } }}
              >
                <Box component="span" sx={{ fontSize: '1.1rem' }}>🇺🇸</Box> {t.language.english}
              </MenuItem>
              <MenuItem
                onClick={() => { setLanguage('pt'); setLangAnchorEl(null); }}
                selected={language === 'pt'}
                sx={{ gap: 1, color: '#fff', '&.Mui-selected': { background: alpha('#8B5CF6', 0.2) } }}
              >
                <Box component="span" sx={{ fontSize: '1.1rem' }}>🇧🇷</Box> {t.language.portuguese}
              </MenuItem>
            </Menu>

            <Button
              onClick={() => navigate('/login')}
              sx={{
                color: 'rgba(255,255,255,0.9)',
                fontWeight: 600,
                borderRadius: 2,
                px: 2.5,
                border: '1px solid rgba(255,255,255,0.2)',
                '&:hover': {
                  border: '1px solid rgba(255,255,255,0.4)',
                  background: 'rgba(255,255,255,0.05)',
                },
              }}
            >
              {t.navbar.login}
            </Button>
            <Button
              onClick={() => navigate('/login')}
              sx={{
                fontWeight: 600,
                borderRadius: 2,
                px: 3,
                color: '#fff',
                background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
                boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)',
                  boxShadow: '0 6px 20px rgba(139, 92, 246, 0.5)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {t.navbar.getStarted}
            </Button>
          </Box>
        )}

        {/* Mobile Hamburger */}
        {isMobile && (
          <IconButton
            onClick={() => setMobileOpen(!mobileOpen)}
            sx={{ color: '#fff' }}
          >
            {mobileOpen ? <CloseIcon /> : <MenuIcon />}
          </IconButton>
        )}
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen && isMobile}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            background: 'linear-gradient(180deg, #0A0E27 0%, #1A1F3A 100%)',
            border: 'none',
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <IconButton onClick={() => setMobileOpen(false)} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <List>
            {navLinks.map((link) => (
              <ListItemButton
                key={link.href}
                onClick={() => handleNavClick(link.href)}
                sx={{ borderRadius: 2, color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff' } }}
              >
                <ListItemText primary={link.label} />
              </ListItemButton>
            ))}
          </List>
          <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Button
              fullWidth
              onClick={() => { setLanguage(language === 'en' ? 'pt' : 'en'); }}
              startIcon={<LanguageIcon />}
              sx={{ color: 'rgba(255,255,255,0.8)', justifyContent: 'flex-start' }}
            >
              {language === 'en' ? '🇧🇷 Português' : '🇺🇸 English'}
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => { setMobileOpen(false); navigate('/login'); }}
              sx={{
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.3)',
                '&:hover': { borderColor: '#8B5CF6' },
              }}
            >
              {t.navbar.login}
            </Button>
            <Button
              fullWidth
              onClick={() => { setMobileOpen(false); navigate('/login'); }}
              sx={{
                color: '#fff',
                background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)' },
              }}
            >
              {t.navbar.getStarted}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default Navbar;
